// Copied from platform-app components/pos/receipt/BlockSettings.tsx on 2026-10-14 for Super Admin → POS Defaults — keep in step with the original.
import React from 'react';
import type { ReceiptBlock } from '@platform/shared-types';

type Props<T extends ReceiptBlock> = { block: T; onChange: (next: T) => void };

const Seg = <V extends string>({ value, options, onChange }: { value: V; options: readonly V[]; onChange: (v: V) => void }) => (
  <div className="inline-flex bg-white border border-stone-300 rounded-lg overflow-hidden">
    {options.map((o) => (
      <button
        key={o}
        type="button"
        onClick={() => onChange(o)}
        className={`px-3 py-1 text-xs ${value === o ? 'bg-accent-600 text-white' : 'text-stone-600'}`}
      >{o}</button>
    ))}
  </div>
);

const Pill = ({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3 py-1 text-[11px] font-semibold rounded-full border ${active ? 'bg-black text-white border-black' : 'bg-white text-stone-600 border-stone-300'}`}
  >{label}</button>
);

export default function BlockSettings<T extends ReceiptBlock>({ block, onChange }: Props<T>) {
  // POS Defaults: loosened for super-admin-ui's TS (platform-app accepted the per-field `as Partial<T>` casts).
  const set = (patch: Record<string, unknown>) => onChange({ ...block, ...patch } as T);

  return (
    <div className="bg-stone-50 border-t border-stone-200 p-3 grid grid-cols-[110px_1fr] gap-y-2 gap-x-3 items-center">
      {(block.type === 'text' || block.type === 'boxed') && (
        <>
          <label className="text-[11px] font-semibold uppercase text-stone-500">Content</label>
          <input
            value={block.content}
            onChange={(e) => set({ content: e.target.value.slice(0, 200) })}
            className="border border-stone-300 rounded-md px-2 py-1 text-sm bg-white text-stone-800"
            maxLength={200}
          />
          <label className="text-[11px] font-semibold uppercase text-stone-500">Font size</label>
          <Seg<'S'|'N'|'L'|'XL'> value={block.size} options={['S','N','L','XL']} onChange={(v) => set({ size: v })} />
          <label className="text-[11px] font-semibold uppercase text-stone-500">Alignment</label>
          <Seg<'L'|'C'|'R'> value={block.align} options={['L','C','R']} onChange={(v) => set({ align: v })} />
          <label className="text-[11px] font-semibold uppercase text-stone-500">Style</label>
          <div className="flex gap-2 flex-wrap">
            <Pill active={block.bold} label="Bold" onClick={() => set({ bold: !block.bold })} />
            {block.type === 'text' && <Pill active={(block as any).italic} label="Italic" onClick={() => set({ italic: !(block as any).italic })} />}
            {block.type === 'text' && <Pill active={(block as any).underline} label="Underline" onClick={() => set({ underline: !(block as any).underline })} />}
            {block.type === 'text' && <Pill active={(block as any).inverse} label="Inverse" onClick={() => set({ inverse: !(block as any).inverse })} />}
          </div>
          {block.type === 'boxed' && (
            <>
              <label className="text-[11px] font-semibold uppercase text-stone-500">Border</label>
              <Seg<'dashed'|'solid'|'double'> value={(block as any).borderStyle} options={['dashed','solid','double']} onChange={(v) => set({ borderStyle: v })} />
            </>
          )}
        </>
      )}

      {block.type === 'logo' && (
        <>
          <label className="text-[11px] font-semibold uppercase text-stone-500">Alignment</label>
          <Seg<'L'|'C'|'R'> value={block.align} options={['L','C','R']} onChange={(v) => set({ align: v })} />
          <label className="text-[11px] font-semibold uppercase text-stone-500">Max width</label>
          <Seg<'S'|'M'|'L'> value={block.maxWidth} options={['S','M','L']} onChange={(v) => set({ maxWidth: v })} />
        </>
      )}

      {block.type === 'image' && (
        <>
          <label className="text-[11px] font-semibold uppercase text-stone-500">Image URL</label>
          <input
            value={block.imageUrl}
            onChange={(e) => set({ imageUrl: e.target.value })}
            className="border border-stone-300 rounded-md px-2 py-1 text-sm bg-white text-stone-800"
            placeholder="https://cdn.example.com/..."
          />
          <label className="text-[11px] font-semibold uppercase text-stone-500">Alignment</label>
          <Seg<'L'|'C'|'R'> value={block.align} options={['L','C','R']} onChange={(v) => set({ align: v })} />
          <label className="text-[11px] font-semibold uppercase text-stone-500">Max width</label>
          <Seg<'S'|'M'|'L'> value={block.maxWidth} options={['S','M','L']} onChange={(v) => set({ maxWidth: v })} />
        </>
      )}

      {block.type === 'qr' && (
        <>
          <label className="text-[11px] font-semibold uppercase text-stone-500">Data / URL</label>
          <input
            value={block.data}
            onChange={(e) => set({ data: e.target.value.slice(0, 500) })}
            className="border border-stone-300 rounded-md px-2 py-1 text-sm bg-white text-stone-800"
          />
          <label className="text-[11px] font-semibold uppercase text-stone-500">Caption</label>
          <input
            value={block.caption || ''}
            onChange={(e) => set({ caption: e.target.value || null })}
            className="border border-stone-300 rounded-md px-2 py-1 text-sm bg-white text-stone-800"
            maxLength={100}
          />
          <label className="text-[11px] font-semibold uppercase text-stone-500">Alignment</label>
          <Seg<'L'|'C'|'R'> value={block.align} options={['L','C','R']} onChange={(v) => set({ align: v })} />
          <label className="text-[11px] font-semibold uppercase text-stone-500">Size</label>
          <Seg<'S'|'M'|'L'> value={block.size} options={['S','M','L']} onChange={(v) => set({ size: v })} />
        </>
      )}

      {block.type === 'divider' && (
        <>
          <label className="text-[11px] font-semibold uppercase text-stone-500">Style</label>
          <Seg<'dashed'|'solid'|'double'> value={block.style} options={['dashed','solid','double']} onChange={(v) => set({ style: v })} />
        </>
      )}

      {block.type === 'spacer' && (
        <>
          <label className="text-[11px] font-semibold uppercase text-stone-500">Height</label>
          <Seg<'S'|'M'|'L'> value={block.height} options={['S','M','L']} onChange={(v) => set({ height: v })} />
        </>
      )}

      {block.type !== 'spacer' && (
        <>
          <label className="text-[11px] font-semibold uppercase text-stone-500">Spacing above</label>
          <Seg<'none'|'S'|'L'> value={(block as any).spacingAbove} options={['none','S','L']} onChange={(v) => set({ spacingAbove: v })} />
        </>
      )}
    </div>
  );
}
